import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable, Subscription, interval, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {BE_API_AUTH_URL} from '../constants/app.constants';
import {AuthService} from './auth.service';

export type NotificationSeverity = 'info' | 'success' | 'warning' | 'error';

export interface AppNotification {
  id: string;
  type: string;
  severity: NotificationSeverity;
  params: Record<string, string>;
  content?: { title?: { en?: string; de?: string }; body?: { en?: string; de?: string } };
  target: { kind?: string; id?: string };
  readAt: string | null;
  createdAt: string;
  countsTowardUnread: boolean;
}

export interface NotificationPage {
  items: AppNotification[];
  nextCursor: string | null;
}

export type NotificationReadState = 'all' | 'unread' | 'read';

@Injectable({providedIn: 'root'})
export class NotificationService implements OnDestroy {
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private pollSubscription?: Subscription;
  readonly unreadCount$ = this.unreadCountSubject.asObservable();

  constructor(private http: HttpClient, private auth: AuthService) {
  }

  start(): void {
    if (this.pollSubscription || !this.auth.isAuthenticated() || !this.auth.isCurrentCompanyUser()) return;
    this.refreshUnreadCount();
    this.pollSubscription = interval(60_000).pipe(switchMap(() => this.getUnreadCount())).subscribe((count) => this.unreadCountSubject.next(count));
  }

  stop(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = undefined;
    this.unreadCountSubject.next(0);
  }

  ngOnDestroy(): void {
    this.stop();
  }

  refreshUnreadCount(): void {
    this.getUnreadCount().subscribe((count) => this.unreadCountSubject.next(count));
  }

  list(readState: NotificationReadState, cursor?: string): Observable<NotificationPage> {
    const query = new URLSearchParams({limit: '20', readState});
    if (cursor) query.set('cursor', cursor);
    const suffix = `?${query.toString()}`;
    return this.http.get<NotificationPage>(`${BE_API_AUTH_URL}/notifications${suffix}`, {headers: this.auth.createAuthorizationHeader()});
  }

  markRead(id: string): Observable<AppNotification> {
    return this.http.patch<AppNotification>(`${BE_API_AUTH_URL}/notifications/${encodeURIComponent(id)}/read`, {}, {headers: this.auth.createAuthorizationHeader()});
  }

  markAllRead(): Observable<void> {
    return this.http.patch<void>(`${BE_API_AUTH_URL}/notifications/read-all`, {}, {headers: this.auth.createAuthorizationHeader()});
  }

  private getUnreadCount(): Observable<number> {
    if (!this.auth.isAuthenticated() || !this.auth.isCurrentCompanyUser()) return of(0);
    return this.http.get<{
      count: number
    }>(`${BE_API_AUTH_URL}/notifications/unread-count`, {headers: this.auth.createAuthorizationHeader()})
      .pipe(map((response) => Number(response?.count || 0)), catchError(() => of(0)));
  }
}
